import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/StatusBadge";
import { EmptyState } from "../../components/EmptyState";
import { SupplierSelect } from "../../components/SupplierSelect";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Search, FileText, ArrowUpDown, X, Plus, MoreHorizontal, Eye, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { INTERNAL_STATUSES, money, fmtDate } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";
import { BulkDeleteDialog } from "../../components/admin/BulkDeleteDialog";

const PAGE_SIZE = 25;

function NewClaimModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ order_number: "", customer_name: "", mobile: "", product_name: "", part_name: "", brand: "", serial_number: "", problem_description: "", url: "" });
  const [files, setFiles] = useState({ serial_photo: null, problem_video: null, invoice_photo: null });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.customer_name || !form.mobile || !form.product_name || !form.serial_number) { toast.error("Customer, WhatsApp, product and serial are required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      if (files.serial_photo) fd.append("serial_photo", files.serial_photo);
      if (files.problem_video) fd.append("problem_video", files.problem_video);
      if (files.invoice_photo) fd.append("invoice_photo", files.invoice_photo);
      const { data } = await api.post("/admin/claims", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${data.rma_number}`);
      onOpenChange(false);
      onCreated(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>New Warranty Claim (Admin)</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="mb-1.5 block text-sm font-medium">Order / Invoice Number</Label><Input data-testid="nc-order" value={form.order_number} onChange={set("order_number")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Customer Name *</Label><Input data-testid="nc-name" value={form.customer_name} onChange={set("customer_name")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">WhatsApp Number *</Label><Input data-testid="nc-mobile" value={form.mobile} onChange={set("mobile")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Product Name *</Label><Input data-testid="nc-product" value={form.product_name} onChange={set("product_name")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Part Name</Label><Input data-testid="nc-part-name" value={form.part_name} onChange={set("part_name")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Brand</Label><Input data-testid="nc-brand" value={form.brand} onChange={set("brand")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Serial Number *</Label><Input data-testid="nc-serial" value={form.serial_number} onChange={set("serial_number")} /></div>
          <div className="sm:col-span-2"><Label className="mb-1.5 block text-sm font-medium">Problem Description</Label><Textarea data-testid="nc-problem" rows={3} value={form.problem_description} onChange={set("problem_description")} /></div>
          <div className="sm:col-span-2"><Label className="mb-1.5 block text-sm font-medium">URL (optional)</Label><Input data-testid="nc-url" placeholder="https://..." value={form.url} onChange={set("url")} /></div>
          <FileInput label="Serial Number Photo" onFile={(f) => setFiles({ ...files, serial_photo: f })} file={files.serial_photo} accept="image/*" />
          <FileInput label="Problem Video" onFile={(f) => setFiles({ ...files, problem_video: f })} file={files.problem_video} accept="video/*" />
          <FileInput label="Invoice Photo" onFile={(f) => setFiles({ ...files, invoice_photo: f })} file={files.invoice_photo} accept="image/*" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-new-claim-btn" className="bg-blue-600 hover:bg-blue-700">{saving ? "Creating..." : "Create Claim"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileInput({ label, onFile, file, accept }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs text-slate-500">{label}</Label>
      <label className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500 hover:border-blue-400">
        <Plus className="h-3.5 w-3.5" /> {file ? file.name : "Optional"}
        <input type="file" accept={accept} className="hidden" onChange={(e) => onFile(e.target.files[0])} />
      </label>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function DeleteClaimDialog({ claim, open, onOpenChange, onDeleted }) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (open) { setStep(1); setConfirmText(""); } }, [open]);

  if (!claim) return null;
  const matches = confirmText.trim().toUpperCase() === (claim.rma_number || "").toUpperCase();

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/claims/${claim.id}`);
      toast.success("Warranty claim deleted successfully.");
      onOpenChange(false);
      onDeleted(claim.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Unable to delete claim");
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="delete-claim-dialog">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-5 w-5" /> Delete Warranty Claim?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              You are about to delete <span className="font-mono font-semibold text-slate-900">{claim.rma_number}</span>. This action may remove related warranty data.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="delete-cancel-btn">Cancel</Button>
              <Button className="bg-rose-600 text-white hover:bg-rose-700" onClick={() => setStep(2)} data-testid="delete-continue-btn">Continue</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-rose-600">Final Confirmation</DialogTitle>
            </DialogHeader>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <DetailRow label="RMA Number" value={claim.rma_number} mono />
              <DetailRow label="Customer Name" value={claim.customer_name} />
              <DetailRow label="Product Name" value={claim.product_name} />
              <DetailRow label="Serial Number" value={claim.serial_number} mono />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Type <span className="font-mono font-semibold text-slate-900">{claim.rma_number}</span> to confirm</Label>
              <Input data-testid="delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={claim.rma_number} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-rose-600 text-white hover:bg-rose-700" disabled={!matches || deleting} onClick={doDelete} data-testid="delete-final-btn">
                {deleting ? "Deleting..." : "DELETE CLAIM"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ClaimsList() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canDelete = hasRole("SUPER_ADMIN");
  const [searchParams] = useSearchParams();
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [claims, setClaims] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [supplierId, setSupplierId] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setClaims(null);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (supplierId) params.set("supplier_id", supplierId);
    params.set("sort_by", sortBy);
    params.set("sort_dir", sortDir);
    const { data } = await api.get(`/admin/claims?${params}`);
    setClaims(data);
    setPage(1);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, status, supplierId, sortBy, sortDir]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortDir("desc"); }
  };

  const clearFilters = () => { setSearch(""); setStatus(""); setSupplierId(""); };

  const pages = Math.max(1, Math.ceil((claims?.length || 0) / PAGE_SIZE));
  const pageRows = claims ? claims.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : null;
  const selCount = Object.keys(selected).length;
  const allFilteredSelected = !!claims?.length && claims.every((c) => selected[c.id]);
  const toggleOne = (c) => setSelected((p) => { const n = { ...p }; if (n[c.id]) delete n[c.id]; else n[c.id] = c.rma_number; return n; });
  const toggleAll = () => setSelected((p) => {
    const n = { ...p };
    claims.forEach((c) => { if (allFilteredSelected) delete n[c.id]; else n[c.id] = c.rma_number; });
    return n;
  });
  const onBulkDone = () => { setSelected({}); load(); };

  return (
    <AdminLayout title="Warranty Claims">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="claims-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RMA, customer, mobile, order, serial, SKU..." className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="claims-status-filter" className="w-[180px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {INTERNAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="w-[220px]"><SupplierSelect value={supplierId} onChange={setSupplierId} placeholder="All suppliers" testId="claims-supplier-filter" /></div>
          {(search || status || supplierId) && <Button variant="ghost" onClick={clearFilters} className="gap-1.5 text-slate-500"><X className="h-4 w-4" /> Clear</Button>}
          <Button onClick={() => setNewOpen(true)} data-testid="new-claim-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> New Claim</Button>
        </div>
      </div>
      <NewClaimModal open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => navigate(`/admin/warranty/claims/${id}`)} />

      {canDelete && selCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold text-slate-900" data-testid="selected-count">{selCount} claim{selCount !== 1 ? "s" : ""} selected</span>
          <Button variant="ghost" size="sm" onClick={() => setSelected({})} data-testid="clear-selection-btn" className="gap-1 text-slate-600"><X className="h-4 w-4" /> Clear Selection</Button>
          <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-delete-btn" className="ml-auto gap-1.5 bg-rose-600 text-white hover:bg-rose-700"><Trash2 className="h-4 w-4" /> Bulk Delete</Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {canDelete && <TableHead className="w-10"><Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} disabled={!claims?.length} data-testid="select-all-claims" aria-label="Select all filtered claims" /></TableHead>}
                <SortableHead label="RMA #" field="rma_number" {...{ sortBy, toggleSort }} />
                <TableHead>Customer</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Serial</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Status</TableHead>
                <SortableHead label="Value" field="claim_value" {...{ sortBy, toggleSort }} />
                <SortableHead label="Days" field="created_at" {...{ sortBy, toggleSort }} />
                <TableHead>Assigned</TableHead>
                <TableHead className="w-12 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {claims === null && Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={12}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))}
              {pageRows?.map((c) => (
                <TableRow key={c.id} data-testid={`claim-row-${c.rma_number}`} onClick={() => navigate(`/admin/warranty/claims/${c.id}`)} className={`cursor-pointer hover:bg-blue-50/50 ${selected[c.id] ? "bg-rose-50/60" : ""}`}>
                  {canDelete && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={!!selected[c.id]} onCheckedChange={() => toggleOne(c)} data-testid={`select-claim-${c.rma_number}`} /></TableCell>}
                  <TableCell className="font-mono text-sm font-semibold text-blue-700">{c.rma_number}</TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900">{c.customer_name}</div>
                    <div className="text-xs text-slate-400">{c.mobile}</div>
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm text-slate-700">{c.product_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.brand || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{c.serial_number}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.supplier_name || "—"}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell className="font-mono text-sm text-slate-700">{money(c.claim_value, c.currency)}</TableCell>
                  <TableCell className="text-center text-sm font-medium text-slate-700">{c.days_open}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.assigned_employee || "—"}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`claim-actions-${c.rma_number}`}><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/warranty/claims/${c.id}`)} data-testid={`view-claim-${c.rma_number}`}><Eye className="mr-2 h-4 w-4" /> View Claim</DropdownMenuItem>
                        {canDelete && <DropdownMenuItem onClick={() => setDeleteTarget(c)} className="text-rose-600 focus:bg-rose-50 focus:text-rose-700" data-testid={`delete-claim-${c.rma_number}`}><Trash2 className="mr-2 h-4 w-4" /> Delete Claim</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {claims?.length === 0 && (
          <EmptyState icon={FileText} title="No claims found" description="Try adjusting your search or filters." testId="claims-empty" />
        )}
      </div>
      {claims && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
          <span data-testid="claims-count">{claims.length} claim{claims.length !== 1 ? "s" : ""}</span>
          {pages > 1 && (
            <div className="flex items-center gap-2" data-testid="claims-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="claims-prev-page">Previous</Button>
              <span className="text-slate-600" data-testid="claims-page-indicator">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="claims-next-page">Next</Button>
            </div>
          )}
        </div>
      )}
      <BulkDeleteDialog open={bulkOpen} onOpenChange={setBulkOpen} selected={Object.entries(selected).map(([id, rma_number]) => ({ id, rma_number }))} onDone={onBulkDone} />
      <DeleteClaimDialog
        claim={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        onDeleted={(id) => { setClaims((prev) => (prev ? prev.filter((x) => x.id !== id) : prev)); setSelected((p) => { const n = { ...p }; delete n[id]; return n; }); }}
      />
    </AdminLayout>
  );
}

function SortableHead({ label, field, sortBy, toggleSort }) {
  return (
    <TableHead onClick={() => toggleSort(field)} className="cursor-pointer select-none hover:text-slate-900">
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown className={`h-3 w-3 ${sortBy === field ? "text-blue-600" : "text-slate-300"}`} /></span>
    </TableHead>
  );
}
